import React, { useState, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Building2, Cog, ShieldCheck, FileText, Upload, Sparkles,
  RotateCcw, ChevronDown, X, ImageIcon,
} from "lucide-react";
import type { AlignmentOutletContext, ContextFormData } from "../AlignmentModule";
import { CONTEXT_PREFILL } from "../norms/normConfig";
import WorkInProgress from "../WorkInProgress";
import "./ContextPage.css";

/* ── Tag input component ── */
interface TagInputProps {
  value: string[];
  onChange: (v: string[]) => void;
  options: string[];
  placeholder: string;
}
const TagInput: React.FC<TagInputProps> = ({ value, onChange, options, placeholder }) => {
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const filtered = options.filter(
    (o) => !value.includes(o) && o.toLowerCase().includes(input.toLowerCase())
  );
  const add = (tag: string) => {
    const t = tag.trim();
    if (t && !value.includes(t)) onChange([...value, t]);
    setInput("");
    setOpen(false);
  };
  const remove = (tag: string) => onChange(value.filter((t) => t !== tag));
  return (
    <div className="ctx-tag-wrap">
      <div className="ctx-tag-box">
        {value.map((t) => (
          <span key={t} className="ctx-tag-chip">
            {t}
            <button type="button" onClick={() => remove(t)}><X size={11} /></button>
          </span>
        ))}
        <input
          value={input}
          onChange={(e) => { setInput(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && input.trim()) { e.preventDefault(); add(input); }
            if (e.key === "Backspace" && !input && value.length) {
              onChange(value.slice(0, -1));
            }
          }}
          placeholder={value.length === 0 ? placeholder : ""}
          className="ctx-tag-input"
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="ctx-tag-dropdown">
          {filtered.map((o) => (
            <button key={o} type="button" onMouseDown={() => add(o)} className="ctx-tag-option">
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Simple select ── */
interface SelectFieldProps {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
}
const SelectField: React.FC<SelectFieldProps> = ({ value, onChange, options, placeholder }) => (
  <div className="ctx-select-wrap">
    <select className="ctx-select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
    <ChevronDown size={14} className="ctx-select-arrow" />
  </div>
);

/* ── Section card ── */
interface SectionProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}
const Section: React.FC<SectionProps> = ({ icon, title, subtitle, children }) => (
  <div className="ctx-section">
    <div className="ctx-section-header">
      <span className="ctx-section-icon">{icon}</span>
      <div>
        <h2 className="ctx-section-title">{title}</h2>
        {subtitle && <p className="ctx-section-subtitle">{subtitle}</p>}
      </div>
    </div>
    <div className="ctx-section-body">{children}</div>
  </div>
);

/* ── Field row ── */
const FieldRow: React.FC<{ label: string; hint?: string; children: React.ReactNode; half?: boolean }> = ({
  label, hint, children, half,
}) => (
  <div className={`ctx-field${half ? " ctx-field--half" : ""}`}>
    <label className="ctx-label">{label}</label>
    {hint && <span className="ctx-hint">{hint}</span>}
    {children}
  </div>
);

/* ── Options data ── */
const SECTORS = [
  "Agriculture & Agroalimentaire", "Industrie manufacturière", "Construction & BTP",
  "Commerce de détail / gros", "Services aux entreprises", "Transport & Logistique",
  "Énergie & Environnement", "Technologies & Numérique", "Finance & Assurance",
  "Santé & Pharmaceutique", "Éducation & Formation", "Hôtellerie & Restauration",
  "Mines & Extraction", "Télécommunications", "Autre",
];
const SIZES = [
  "Moins de 50 salariés", "50 - 249 salariés", "250 - 999 salariés",
  "1 000 - 4 999 salariés", "5 000 salariés et plus",
];
const COUNTRIES = [
  "Maroc", "France", "Espagne", "Belgique", "Allemagne", "Italie", "Sénégal",
  "Côte d'Ivoire", "Tunisie", "Algérie", "Égypte", "Afrique du Sud", "Autre",
];
const ENV_IMPACTS = [
  "Consommation d'eau", "Émissions GES scope 1 & 2", "Émissions scope 3",
  "Déchets dangereux", "Déchets non dangereux", "Consommation d'énergie",
  "Pollution des sols", "Biodiversité", "Utilisation des terres",
];
const SOCIAL_PROFILES = [
  "Saisonniers agricoles", "Travailleurs migrants", "Femmes en majorité",
  "Jeunes / apprentis", "Handicapés", "Populations vulnérables",
  "Travailleurs postés / nuit", "Personnel sous-traité",
];
const SUPPLIER_TYPES = [
  "Matières premières agricoles", "Emballages plastique / verre",
  "Transport et logistique", "Chimie et intrants", "Services numériques",
  "Construction et maintenance", "Sous-traitance industrielle",
];
const RSE_FUNCTIONS = [
  "Directeur RSE / Développement durable", "Responsable Environnement HSE",
  "DRH / Responsable RH", "DAF / Responsable Finance",
  "Responsable Qualité", "Directeur Achats", "Responsable Communication",
  "Direction Générale", "Comité RSE dédié",
];
const RSE_POLICIES = [
  "Politique environnementale", "Politique droits humains", "Politique santé-sécurité",
  "Charte sociale", "Code d'éthique", "Politique achats durables",
  "Politique anticorruption", "Politique diversité-inclusion",
  "Politique changement climatique",
];
const CERTIFICATIONS = [
  "ISO 14001", "ISO 45001", "ISO 50001", "ISO 26000",
  "SA8000", "GlobalGAP", "Rainforest Alliance", "Fair Trade",
  "B Corp", "EMAS", "ISO 9001", "CDP", "SBTi",
];
const ESG_INDICATORS = [
  "GES scope 1 & 2 (tCO2eq)", "GES scope 3", "Consommation énergie (MWh)",
  "Part énergies renouvelables (%)", "Consommation eau (m³)",
  "Déchets totaux (tonnes)", "Taux valorisation déchets (%)",
  "Taux fréquence accidents (TF)", "Taux gravité accidents (TG)",
  "Heures de formation / salarié", "Taux d'absentéisme (%)",
  "Index égalité H/F", "% femmes à des postes d'encadrement",
];
const ETHICS_TOPICS = [
  "Protection des données personnelles (RGPD)", "Lutte contre la corruption",
  "Conflits d'intérêts", "Cybersécurité", "Conformité fiscale",
  "Respect du droit de la concurrence", "Signalement éthique (lanceurs d'alerte)",
  "Propriété intellectuelle",
];
const DOC_MATURITY = [
  "Documents sources", "Données à consolider", "Preuves à relier",
  "Données estimées", "Données certifiées tiers", "Données incomplètes",
];
const DATA_AVAILABILITY = [
  "Données extraites des SI", "Données à confirmer",
  "Données à collecter auprès des sites", "Données fournisseurs à obtenir",
  "Données non disponibles", "Données partiellement disponibles",
];
const GRI_OBJECTIVES = [
  "Rapport RSE annuel", "Conformité GRI Standards",
  "Communication sur le progrès (COP)", "Benchmarking concurrentiel",
  "Préparation à la CSRD", "Dialogue parties prenantes",
  "Exigence contractuelle client", "Exigence investisseur",
];
const GRI_PERIODS = [
  "Exercice fiscal 2024 (1er janvier – 31 décembre 2024)",
  "Exercice fiscal 2023 (1er janvier – 31 décembre 2023)",
  "Exercice fiscal 2022 (1er janvier – 31 décembre 2022)",
  "Année civile 2024", "Exercice décalé 2023/2024",
];
const ASSURANCE = [
  "Non", "Assurance limitée (niveau 1)", "Assurance raisonnable (niveau 2)",
  "Vérification interne uniquement",
];
const MATERIAL_TOPICS = [
  "Eau et effluents", "Émissions GES et changement climatique",
  "Biodiversité et écosystèmes", "Déchets et économie circulaire",
  "Énergie et efficacité", "Santé et sécurité au travail",
  "Conditions de travail et droits sociaux", "Formation et développement des compétences",
  "Diversité, équité et inclusion", "Droits humains et chaîne d'approvisionnement",
  "Anticorruption et éthique", "Gouvernance et transparence",
  "Communautés locales et développement économique",
  "Responsabilité produit et innovation durable",
];

const ContextPage: React.FC = () => {
  const { selectedNorm, contextData, setContextData } = useOutletContext<AlignmentOutletContext>();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  if (selectedNorm !== "GRI") return <WorkInProgress normLabel={selectedNorm} />;

  const set = <K extends keyof ContextFormData>(key: K, value: ContextFormData[K]) =>
    setContextData((prev) => ({ ...prev, [key]: value }));

  const handlePrefill = () => {
    setContextData((prev) => ({ ...prev, ...CONTEXT_PREFILL }));
  };

  const handleReset = () => {
    setContextData({
      companyName: "", sector: "", size: "",
      countries: [], scope: "",
      mainActivities: [], environmentalImpacts: [], socialProfile: [], criticalSuppliers: [],
      rseFunctions: [], rsePolicies: [], certifications: [], esgIndicators: [],
      ethicsTopics: [], docMaturity: [], dataAvailability: [],
      riskSites: "", esgPriorities: "",
      gri_reportingObjective: "", gri_reportingPeriod: "", gri_externalAssurance: "",
      gri_reportingBoundaries: "", gri_materialityProcess: "",
      gri_materialTopics: [], gri_sectorStandards: "", gri_dataOwners: "",
    });
    setLogoPreview(null);
  };

  const handleLogoChange = (file: File) => {
    const url = URL.createObjectURL(file);
    setLogoPreview(url);
  };

  return (
    <div className="ctx-page">
      {/* ── Page header with actions ── */}
      <div className="ctx-page-header">
        <div className="ctx-page-header-text">
          <h1 className="ctx-page-title">Contexte entreprise</h1>
          <p className="ctx-page-desc">
            Renseignez les informations contextuelles de votre organisation pour alimenter l'évaluation GRI.
          </p>
        </div>
        <div className="ctx-header-actions">
          <label className="ctx-action-btn ctx-action-btn--upload" title="Importer un fichier de contexte">
            <Upload size={15} />
            <span>Importer</span>
            <input type="file" accept=".pdf,.docx,.xlsx" style={{ display: "none" }} />
          </label>
          <button className="ctx-action-btn ctx-action-btn--prefill" onClick={handlePrefill}>
            <Sparkles size={15} />
            <span>Pre fill</span>
          </button>
          <button className="ctx-action-btn ctx-action-btn--reset" onClick={handleReset}>
            <RotateCcw size={15} />
            <span>Reset</span>
          </button>
        </div>
      </div>

      {/* ── Section 1: Identity ── */}
      <Section
        icon={<Building2 size={20} />}
        title="Identité & Périmètre"
        subtitle="Informations générales sur l'organisation et le périmètre de l'évaluation."
      >
        <div className="ctx-grid ctx-grid--3">
          <FieldRow label="Nom de l'entreprise">
            <input
              className="ctx-input"
              value={contextData.companyName}
              onChange={(e) => set("companyName", e.target.value)}
              placeholder="Ex : Agro-Industries Maroc S.A."
            />
          </FieldRow>
          <FieldRow label="Secteur d'activité">
            <SelectField
              value={contextData.sector}
              onChange={(v) => set("sector", v)}
              options={SECTORS}
              placeholder="Sélectionnez un secteur"
            />
          </FieldRow>
          <FieldRow label="Taille / effectif">
            <SelectField
              value={contextData.size}
              onChange={(v) => set("size", v)}
              options={SIZES}
              placeholder="Sélectionnez une taille"
            />
          </FieldRow>
        </div>
        <FieldRow label="Pays concernés">
          <TagInput
            value={contextData.countries}
            onChange={(v) => set("countries", v)}
            options={COUNTRIES}
            placeholder="Sélectionnez les pays ou ajoutez une valeur."
          />
        </FieldRow>
        <FieldRow label="Périmètre de l'évaluation" hint="Entités, sites, activités, filiales ou familles de produits couvertes.">
          <textarea
            className="ctx-textarea"
            value={contextData.scope}
            onChange={(e) => set("scope", e.target.value)}
            placeholder="Entités incluses/exclues, sites, activités amont/aval, critères de consolidation."
            rows={3}
          />
        </FieldRow>
      </Section>

      {/* ── Section 2: Operations ── */}
      <Section
        icon={<Cog size={20} />}
        title="Opérations"
        subtitle="Activités principales et profil de risque opérationnel."
      >
        <div className="ctx-grid ctx-grid--2">
          <FieldRow label="Activités principales">
            <TagInput
              value={contextData.mainActivities}
              onChange={(v) => set("mainActivities", v)}
              options={["Production agricole", "Transformation agroalimentaire", "Conditionnement", "Distribution", "Prestation de services", "Commerce", "Extraction", "Construction", "R&D"]}
              placeholder="Sélectionnez les activités ou ajoutez une valeur."
            />
          </FieldRow>
          <FieldRow label="Impacts environnementaux majeurs">
            <TagInput
              value={contextData.environmentalImpacts}
              onChange={(v) => set("environmentalImpacts", v)}
              options={ENV_IMPACTS}
              placeholder="Sélectionnez les impacts ou ajoutez une valeur."
            />
          </FieldRow>
          <FieldRow label="Profil social et SST">
            <TagInput
              value={contextData.socialProfile}
              onChange={(v) => set("socialProfile", v)}
              options={SOCIAL_PROFILES}
              placeholder="Sélectionnez les profils ou ajoutez une valeur."
            />
          </FieldRow>
          <FieldRow label="Achats et fournisseurs critiques">
            <TagInput
              value={contextData.criticalSuppliers}
              onChange={(v) => set("criticalSuppliers", v)}
              options={SUPPLIER_TYPES}
              placeholder="Sélectionnez les familles ou ajoutez une valeur."
            />
          </FieldRow>
        </div>
      </Section>

      {/* ── Section 3: ESG ── */}
      <Section
        icon={<ShieldCheck size={20} />}
        title="ESG & Preuves"
        subtitle="Maturité RSE, certifications, données disponibles et profil de risque."
      >
        <div className="ctx-grid ctx-grid--2">
          <FieldRow label="Fonctions responsables RSE">
            <TagInput
              value={contextData.rseFunctions}
              onChange={(v) => set("rseFunctions", v)}
              options={RSE_FUNCTIONS}
              placeholder="Sélectionnez les fonctions ou ajoutez une valeur."
            />
          </FieldRow>
          <FieldRow label="Politiques RSE existantes">
            <TagInput
              value={contextData.rsePolicies}
              onChange={(v) => set("rsePolicies", v)}
              options={RSE_POLICIES}
              placeholder="Sélectionnez les politiques ou ajoutez une valeur."
            />
          </FieldRow>
          <FieldRow label="Certifications ou référentiels déjà suivis">
            <TagInput
              value={contextData.certifications}
              onChange={(v) => set("certifications", v)}
              options={CERTIFICATIONS}
              placeholder="Sélectionnez les référentiels ou ajoutez une valeur."
            />
          </FieldRow>
          <FieldRow label="Indicateurs ESG suivis">
            <TagInput
              value={contextData.esgIndicators}
              onChange={(v) => set("esgIndicators", v)}
              options={ESG_INDICATORS}
              placeholder="Sélectionnez les indicateurs ou ajoutez une valeur."
            />
          </FieldRow>
          <FieldRow label="Éthique, conformité et données">
            <TagInput
              value={contextData.ethicsTopics}
              onChange={(v) => set("ethicsTopics", v)}
              options={ETHICS_TOPICS}
              placeholder="Sélectionnez les sujets ou ajoutez une valeur."
            />
          </FieldRow>
        </div>

        <div className="ctx-grid ctx-grid--2">
          <FieldRow label="Maturité documentaire">
            <TagInput
              value={contextData.docMaturity}
              onChange={(v) => set("docMaturity", v)}
              options={DOC_MATURITY}
              placeholder="Sélectionnez le niveau de maturité ou ajoutez une valeur."
            />
          </FieldRow>
          <FieldRow label="Disponibilité des données ESG">
            <TagInput
              value={contextData.dataAvailability}
              onChange={(v) => set("dataAvailability", v)}
              options={DATA_AVAILABILITY}
              placeholder="Sélectionnez la disponibilité ou ajoutez une valeur."
            />
          </FieldRow>
        </div>

        <FieldRow label="Sites, activités ou filières à risque" hint="Zones géographiques sensibles, activités saisonnières, intrants critiques.">
          <textarea
            className="ctx-textarea"
            value={contextData.riskSites}
            onChange={(e) => set("riskSites", e.target.value)}
            placeholder="Ex : sites consommateurs d'eau, activités saisonnières, fournisseurs critiques, intrants chimiques."
            rows={3}
          />
        </FieldRow>

        <FieldRow label="Priorités ESG et contraintes documentaires" hint="Priorités de l'évaluation, documents difficiles à obtenir.">
          <textarea
            className="ctx-textarea"
            value={contextData.esgPriorities}
            onChange={(e) => set("esgPriorities", e.target.value)}
            placeholder="Priorités de l'évaluation, documents difficiles à obtenir, sites à risque, contraintes de calendrier."
            rows={3}
          />
        </FieldRow>

        {/* Logo upload */}
        <FieldRow label="Logo de l'entreprise" hint="Utilisé dans les documents Word générés.">
          <div
            className="ctx-logo-zone"
            onClick={() => logoInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files[0];
              if (f) handleLogoChange(f);
            }}
          >
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" className="ctx-logo-preview" />
            ) : (
              <>
                <ImageIcon size={28} color="#9ca3af" />
                <span>Drag and drop file here</span>
                <button
                  type="button"
                  className="ctx-browse-btn"
                  onClick={(e) => { e.stopPropagation(); logoInputRef.current?.click(); }}
                >
                  Browse file
                </button>
              </>
            )}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogoChange(f); }}
            />
          </div>
        </FieldRow>
      </Section>

      {/* ── Section 4: GRI Reporting & Materiality ── */}
      <Section
        icon={<FileText size={20} />}
        title="Reporting GRI & Matérialité"
        subtitle="Paramètres de reporting GRI et processus de matérialité."
      >
        <div className="ctx-grid ctx-grid--3">
          <FieldRow label="Objectif de reporting GRI">
            <SelectField
              value={contextData.gri_reportingObjective}
              onChange={(v) => set("gri_reportingObjective", v)}
              options={GRI_OBJECTIVES}
              placeholder="A renseigner"
            />
          </FieldRow>
          <FieldRow label="Période de reporting">
            <SelectField
              value={contextData.gri_reportingPeriod}
              onChange={(v) => set("gri_reportingPeriod", v)}
              options={GRI_PERIODS}
              placeholder="A renseigner"
            />
          </FieldRow>
          <FieldRow label="Assurance externe">
            <SelectField
              value={contextData.gri_externalAssurance}
              onChange={(v) => set("gri_externalAssurance", v)}
              options={ASSURANCE}
              placeholder="A renseigner"
            />
          </FieldRow>
        </div>

        <FieldRow label="Limites de reporting et chaîne de valeur">
          <textarea
            className="ctx-textarea"
            value={contextData.gri_reportingBoundaries}
            onChange={(e) => set("gri_reportingBoundaries", e.target.value)}
            placeholder="Entités incluses/exclues, sites, activités amont/aval, critères de consolidation."
            rows={3}
          />
        </FieldRow>

        <FieldRow label="Processus de matérialité">
          <textarea
            className="ctx-textarea"
            value={contextData.gri_materialityProcess}
            onChange={(e) => set("gri_materialityProcess", e.target.value)}
            placeholder="Sources analysées, consultations parties prenantes, cotation des impacts, validation gouvernance."
            rows={3}
          />
        </FieldRow>

        <FieldRow label="Thèmes matériels pressentis">
          <TagInput
            value={contextData.gri_materialTopics}
            onChange={(v) => set("gri_materialTopics", v)}
            options={MATERIAL_TOPICS}
            placeholder="Sélectionnez les thèmes GRI ou ajoutez une valeur."
          />
        </FieldRow>

        <FieldRow label="Standards sectoriels applicables" hint="Ex: GRI 13 Agriculture, Aquaculture and Fishing Sectors.">
          <textarea
            className="ctx-textarea"
            value={contextData.gri_sectorStandards}
            onChange={(e) => set("gri_sectorStandards", e.target.value)}
            placeholder="Ex : GRI 13 Agriculture, Aquaculture and Fishing Sectors ; justification si non applicable."
            rows={2}
          />
        </FieldRow>

        <FieldRow label="Propriétaires des données et circuit de validation">
          <textarea
            className="ctx-textarea"
            value={contextData.gri_dataOwners}
            onChange={(e) => set("gri_dataOwners", e.target.value)}
            placeholder="Responsables par thème : RSE, RH, HSE, Finance, Achats, DSI, Direction technique."
            rows={3}
          />
        </FieldRow>
      </Section>
    </div>
  );
};

export default ContextPage;
